import cx from 'classnames';
import { type ReactNode } from 'react';

import {
  CHAT_PANEL_CARD_ID,
  CHAT_PANEL_CARD_WIDTH,
} from './chatPanelConstants';

interface ChatPanelExpandedProps {
  children: ReactNode;
  ariaLabel: string;
}

/**
 * The chat card itself — a fixed 380px-wide column with 20px
 * corner rounding, a white surface, and a two-layer drop shadow.
 *
 * `CHAT_PANEL_CARD_ID` is set on the root so in-panel modals
 * (`MessageFeedback`) can portal into the card and align their
 * backdrop to it with `position: absolute; inset: 0`. The shared
 * constant lives in `chatPanelConstants.ts` so consumers don't
 * have to import this component just to grab the id.
 */
export function ChatPanelExpanded({
  children,
  ariaLabel,
}: ChatPanelExpandedProps) {
  return (
    <div
      id={CHAT_PANEL_CARD_ID}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className={cx(
        'tw-relative',
        'tw-shrink-0',
        'tw-h-full',
        'tw-flex',
        'tw-flex-col',
        'tw-overflow-hidden',
        'tw-pointer-events-auto',
      )}
      style={{
        // Width / radius / shadow don't match the design-system scale
        // (widths jump 320 → 480; the radius scale skips 20), so they
        // stay inline. Width comes from the shared constant so it can't
        // drift from `ChatPanelOverlay`'s position calc. The surface uses
        // the themeable `--surface-primary-elevated` token so the card
        // follows light/dark mode.
        width: CHAT_PANEL_CARD_WIDTH,
        borderRadius: 20,
        boxShadow:
          '0 20px 60px -10px rgba(0,0,0,0.18), 0 8px 24px -6px rgba(0,0,0,0.10)',
        background: 'var(--surface-primary-elevated)',
      }}
    >
      {children}
    </div>
  );
}
